import React, { useState, useEffect } from 'react';
import { 
  X, ChevronRight, ChevronLeft, Stethoscope, ScanEye, Calculator, 
  Pill, ShieldCheck, Euro, BookOpen, History, Sparkles, CheckCircle,
  Mic, Image, AlertTriangle, FileText
} from 'lucide-react';
import { Button } from './ui/button';

const ONBOARDING_KEY = 'hippocrate_onboarding_completed';

const GUIDE_STEPS = [
  {
    id: 'welcome',
    icon: Sparkles,
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-900/30 border-amber-500/50',
    title: 'Bienvenue sur Hippocrate MG',
    description: 'Votre assistant médical IA conçu pour les médecins français.',
    details: [
      'Aide au diagnostic basée sur les recommandations HAS',
      'Sources médicales françaises fiables (CRAT, BDPM, ANSM)',
      'Calcul automatique des cotations CCAM/NGAP',
      'Interface optimisée pour la consultation'
    ]
  },
  {
    id: 'diagnosis',
    icon: Stethoscope,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-900/30 border-blue-500/50',
    title: 'Assistant Diagnostic',
    description: 'Décrivez les symptômes et obtenez une aide au diagnostic structurée.',
    details: [
      'Dictée vocale pour saisie rapide',
      'Hypothèses diagnostiques hiérarchisées',
      'Examens complémentaires recommandés',
      'Signes d\'alerte à surveiller',
      'Liens directs vers les scores médicaux'
    ],
    tip: 'Utilisez Ctrl+Enter pour lancer l\'analyse rapidement'
  },
  {
    id: 'dermato',
    icon: ScanEye,
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-900/30 border-purple-500/50',
    title: 'Dermato IA',
    description: 'Analysez des lésions cutanées avec l\'intelligence artificielle.',
    details: [
      'Upload de photos de lésions',
      'Analyse par GPT-4 Vision',
      'Images de référence DermNet NZ',
      'Diagnostic différentiel illustré'
    ],
    tip: 'Prenez des photos bien éclairées, de près et de loin'
  },
  {
    id: 'scores',
    icon: Calculator,
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-900/30 border-emerald-500/50',
    title: 'Scores Médicaux',
    description: '51+ calculateurs cliniques avec cotation CCAM intégrée.',
    details: [
      'CHA₂DS₂-VASc, HAS-BLED, CURB-65...',
      'Calcul automatique et interprétation',
      'Cotation CCAM associée',
      'Favoris pour accès rapide (5 max)',
      'Recherche vocale'
    ],
    tip: 'Ajoutez vos scores fréquents en favoris'
  },
  {
    id: 'medications',
    icon: Pill,
    iconColor: 'text-orange-400',
    iconBg: 'bg-orange-900/30 border-orange-500/50',
    title: 'Médicaments',
    description: 'Base de données médicamenteuse française.',
    details: [
      'Recherche par DCI ou nom commercial',
      'Posologies et contre-indications',
      'Interactions médicamenteuses',
      'Liens vers BDPM et ANSM'
    ]
  },
  /* Désactivé temporairement : Vérificateur d'Ordonnances
  {
    id: 'prescription',
    icon: ShieldCheck,
    iconColor: 'text-teal-400',
    iconBg: 'bg-teal-900/30 border-teal-500/50',
    title: 'Vérificateur d\'Ordonnances',
    description: 'Sécurisez vos prescriptions avant de les valider.',
    details: [
      'Détection des allergies croisées',
      'Interactions médicamenteuses critiques',
      'Contre-indications (grossesse, IR, IH)',
      'Upload et analyse de PDF',
      'Sources: ANSM, Thériaque, CRAT'
    ],
    tip: 'Renseignez toujours les allergies connues'
  },
  */
  {
    id: 'cotation',
    icon: Euro,
    iconColor: 'text-yellow-400',
    iconBg: 'bg-yellow-900/30 border-yellow-500/50',
    title: 'Cotation',
    description: 'Aide à la cotation CCAM et NGAP.',
    details: [
      'Recherche d\'actes par mot-clé',
      'Tarifs conventionnels à jour',
      'Majorations applicables',
      'Export pour facturation'
    ]
  },
  {
    id: 'resources',
    icon: BookOpen,
    iconColor: 'text-indigo-400',
    iconBg: 'bg-indigo-900/30 border-indigo-500/50',
    title: 'Biblio & Ressources',
    description: 'Accès rapide aux sources médicales françaises.',
    details: [
      '39 sociétés savantes référencées',
      'HAS, ANSM, CRAT, BDPM...',
      'Recommandations par spécialité',
      'Liens directs vers les guidelines'
    ]
  },
  {
    id: 'ready',
    icon: CheckCircle,
    iconColor: 'text-green-400',
    iconBg: 'bg-green-900/30 border-green-500/50',
    title: 'Vous êtes prêt !',
    description: 'Commencez à utiliser Hippocrate MG pour vos consultations.',
    details: [
      'Vos données restent confidentielles',
      'L\'IA ne remplace pas votre jugement clinique',
      'Vérifiez toujours les recommandations',
      'N\'hésitez pas à explorer toutes les fonctionnalités'
    ],
    isLast: true
  }
];

export const OnboardingGuide = ({ onComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Check if onboarding was already completed
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed) {
      setIsOpen(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < GUIDE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsOpen(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsOpen(false);
    if (onComplete) onComplete();
  };

  // Function to reset onboarding (for testing or user request)
  const resetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_KEY);
    setCurrentStep(0);
    setIsOpen(true);
  };

  if (!isOpen) return null;

  const step = GUIDE_STEPS[currentStep];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / GUIDE_STEPS.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-slate-800">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Close/Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
          title="Passer le guide"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 pt-8">
          {/* Step indicator */}
          <div className="flex justify-center gap-1.5 mb-6">
            {GUIDE_STEPS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'w-6 bg-blue-500' 
                    : idx < currentStep 
                    ? 'bg-blue-500/50' 
                    : 'bg-slate-700'
                }`}
              />
            ))}
          </div>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className={`w-16 h-16 rounded-2xl ${step.iconBg} border flex items-center justify-center`}>
              <Icon className={`w-8 h-8 ${step.iconColor}`} />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white text-center mb-2">
            {step.title}
          </h2>

          {/* Description */}
          <p className="text-slate-400 text-center mb-6">
            {step.description}
          </p>

          {/* Details */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-4">
            <ul className="space-y-2">
              {step.details.map((detail, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{detail}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Tip */}
          {step.tip && (
            <div className="flex items-start gap-2 p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg mb-4">
              <Sparkles className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-200">
                <span className="font-semibold">Astuce :</span> {step.tip}
              </p>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex gap-3 mt-6">
            {currentStep > 0 && (
              <Button
                onClick={handlePrev}
                variant="outline"
                className="flex-1 border-slate-700 hover:bg-slate-800"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
            )}
            
            <Button
              onClick={handleNext}
              className={`flex-1 ${
                step.isLast 
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500' 
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500'
              }`}
            >
              {step.isLast ? (
                <>
                  Commencer
                  <Sparkles className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Skip link */}
          {!step.isLast && (
            <button
              onClick={handleSkip}
              className="w-full mt-3 text-sm text-slate-500 hover:text-slate-400 transition-colors"
            >
              Passer le guide
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

// Export function to manually trigger onboarding (for settings/help menu)
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_KEY);
  window.location.reload();
};

export const isOnboardingCompleted = () => {
  return localStorage.getItem(ONBOARDING_KEY) === 'true';
};
